import { ensureAssetPaintOverlay, refreshAssetPaintOverlay, tagAndPaintAssetRisk } from "./assetRiskUtils";
import { removeBuffersForPolyId, syncBuffersFor } from "./bufferUtils";
import { ensureDrawAboveLine, handleDrawBuffer, handleDrawPoint } from "./drawUtils";
import { lineCoordsToPolygonFeature, updateLineFromExistingPolygon } from "./lineUtils";
import { extractBaseRing, getCurrentLotRing, safeDeleteLot, setCurrentLotRing } from "./utils";

let globalLotPerimeterUpdateHandler = null;
let globalAssetContourUpdateHandler = null;
let globalHandlersInitialized = false;

export const registerDrawHandler = (
  map,
  draw,
  setFireFenceParams,
  setInitialAsessmentParams,
  setAnalysedParams,
  setDrawMode,
  whichMode = 'area',
  bufferDistances = [],
  setPolygon = null,
  setExpanded = null
) => {
  let cleanupFns = [];

  if (!globalHandlersInitialized) {
    initializeGlobalHandlers(map, draw, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams, bufferDistances);
    globalHandlersInitialized = true;
  }

  if (whichMode === 'area') {
    const { onCreate, onDelete, destroy } =
      makeLotPerimeterHandler(map, draw, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams, setDrawMode);

    const existingLotPerimeter = draw.getAll()?.features?.find(f => f?.properties?.lotPerimeter);

    if (existingLotPerimeter) {
      updateLineFromExistingPolygon(map, existingLotPerimeter, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams);
    }

    map.on('draw.create', onCreate);
    map.on('draw.delete', onDelete);

    cleanupFns.push(() => {
      map.off('draw.create', onCreate);
      map.off('draw.delete', onDelete);
      destroy();
    });

    return () => cleanupFns.forEach(fn => fn());
  }

  if (whichMode === 'home') {
    const wichHandle = handleDrawBuffer(map, setDrawMode, bufferDistances, setPolygon, setExpanded);

    const syncCreate = (e) => e.features?.forEach(f => {
      if (f.properties?.assetContour || f.properties?.isBuilding) {
        f.properties.buffers = bufferDistances;
        syncBuffersFor(f, map, bufferDistances);
      }
    });

    map.on('draw.create', syncCreate);
    map.on('draw.create', wichHandle);

    return () => {
      map.off('draw.create', syncCreate);
      map.off('draw.create', wichHandle);
    };
  }

  if (whichMode === 'point') {
    const h = handleDrawPoint(setDrawMode);
    map.on('draw.create', h);
    return () => map.off('draw.create', h);
  }
};

export function makeLotPerimeterHandler(map, draw, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams, setDrawMode) {
  let lastRingCoords = null;
  let lineClickBound = false;
  let currentLotPerimeterId = null;
  let lineClickEnabled = true;

  const enter = () => map.getCanvas().style.cursor = 'pointer';
  const leave = () => map.getCanvas().style.cursor = '';

  const onSelectionChange = (e) => {
    if (!currentLotPerimeterId) return;

    const selectedIds = typeof draw.getSelectedIds === 'function'
      ? draw.getSelectedIds()
      : (e?.features || []).map(f => f.id);

    const stillSelected = selectedIds.includes(currentLotPerimeterId);
    if (stillSelected) return;

    safeDeleteLot(draw, map, currentLotPerimeterId);

    currentLotPerimeterId = null;
    if (map.getLayer('line')) {
      attachLineEvents();
    }
  };

  map.on('draw.selectionchange', onSelectionChange);

  const detachLineEvents = () => {
    if (!lineClickBound) return;
    if (map.getLayer('line')) {
      map.off('click', 'line', onLineClick);
      map.off('mouseenter', 'line', enter);
      map.off('mouseleave', 'line', leave);
    }
    lineClickBound = false;
  };

  const attachLineEvents = () => {
    if (lineClickBound || !map.getLayer('line')) return;
    if (!lineClickEnabled) return;

    map.on('click', 'line', onLineClick);
    map.on('mouseenter', 'line', enter);
    map.on('mouseleave', 'line', leave);
    lineClickBound = true;
  };

  const onLineClick = () => {
    if (!lineClickEnabled) return;

    const existingLotPerimeter = draw.getAll()?.features?.find(f => f?.properties?.lotPerimeter);
    if (existingLotPerimeter) {
      draw.changeMode('direct_select', { featureId: existingLotPerimeter.id });
      currentLotPerimeterId = existingLotPerimeter.id;
      ensureDrawAboveLine(map);
      lineClickEnabled = false;
      setTimeout(() => { lineClickEnabled = true; }, 300);
      return;
    }

    const ring = getCurrentLotRing(map);
    if (!ring || ring.length < 3) return;

    const poly = lineCoordsToPolygonFeature(ring, {
      lotPerimeter: true,
      scope: 'area',
      assetContour: false
    });

    const added = draw.add(poly);
    const polyId = Array.isArray(added) ? added[0] : added;
    currentLotPerimeterId = polyId;

    detachLineEvents();
    map.once('draw.render', () => {
      draw.changeMode('direct_select', { featureId: polyId });
      ensureDrawAboveLine(map);
      setTimeout(() => {
        lineClickEnabled = true;
      }, 300);
    });
  };

  const onCreate = (e) => {
    const f = e?.features?.[0];

    if (!f) return;

    if (f.properties?.lotPerimeter) {
      currentLotPerimeterId = f.id;
      updateLineFromExistingPolygon(map, f, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams);
      return;
    }

    const ring = extractBaseRing(f.geometry);
    setCurrentLotRing(map, ring);

    if (f.geometry?.type === 'Polygon') {
      const ring = f.geometry.coordinates?.[0] || [];
      lastRingCoords = ring.slice();
    } else if (f.geometry?.type === 'LineString') {
      lastRingCoords = f.geometry.coordinates.slice();
    }
    
    updateLineFromExistingPolygon(map, f, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams);

    attachLineEvents();

    draw.delete(f.id);
    setDrawMode?.(prev => ({ ...prev, area: 'simple_select' }));
  };

  const onDelete = (e) => {
    const deletedPerimeter = (e?.features || []).find(x => x.properties?.lotPerimeter);
    if (deletedPerimeter) {
      currentLotPerimeterId = null;
      lineClickEnabled = true;

      setTimeout(() => {
        if (map.getLayer('line')) {
          attachLineEvents();
        }
      }, 100);
    }
  };

  const destroy = () => {
    //detachLineEvents();
    lineClickEnabled = true;
  };

  return { onCreate, onDelete, destroy };
}

export const initializeGlobalHandlers = (map, draw, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams, bufferDistances) => {
  if (!globalLotPerimeterUpdateHandler) {
    globalLotPerimeterUpdateHandler = (e) => {
      const lotPerimeterFeature = e.features?.find(f => f?.properties?.lotPerimeter);
      if (lotPerimeterFeature) {
        updateLineFromExistingPolygon(map, lotPerimeterFeature, setFireFenceParams, setInitialAsessmentParams, setAnalysedParams);
      }
    };

    map.on('draw.update', globalLotPerimeterUpdateHandler);
    map.on('draw.render', globalLotPerimeterUpdateHandler);
  }

  if (!globalAssetContourUpdateHandler) {
    globalAssetContourUpdateHandler = (e) => {
      e.features?.forEach(f => {
        if (f.properties?.assetContour || f.properties?.isBuilding) {
          const polyBuffers = f.properties?.buffers || bufferDistances;
          try { tagAndPaintAssetRisk(map, draw, f); } catch { }
          syncBuffersFor(f, map, polyBuffers);
          refreshAssetPaintOverlay(map, draw);
        }
      });
    };

    map.on('draw.update', globalAssetContourUpdateHandler);
    map.on('draw.render', globalAssetContourUpdateHandler);
  }

  const globalDeleteHandler = (e) => {
    e.features?.forEach(f => {
      if (f.properties?.assetContour || f.properties?.isBuilding) {
        removeBuffersForPolyId(map, f.id);
      }
      // if (f.properties?.lotPerimeter) {
      //   if (map.getLayer('line')) map.removeLayer('line');
      //   if (map.getLayer('line-outline')) map.removeLayer('line-outline');
      //   if (map.getSource('line')) map.removeSource('line');
      // }
    });
  };

  ensureAssetPaintOverlay(map);
  refreshAssetPaintOverlay(map, draw);

  map.on('draw.delete', globalDeleteHandler);
};

export const clearAllHandlers = (map) => {
  if (!map) return;

  if (globalLotPerimeterUpdateHandler) {
    map.off('draw.update', globalLotPerimeterUpdateHandler);
    map.off('draw.render', globalLotPerimeterUpdateHandler);
    globalLotPerimeterUpdateHandler = null;
  }

  if (globalAssetContourUpdateHandler) {
    map.off('draw.update', globalAssetContourUpdateHandler);
    map.off('draw.render', globalAssetContourUpdateHandler);
    globalAssetContourUpdateHandler = null;
  }

  globalHandlersInitialized = false;
};

export const clearLayers = (map) => {
  if (globalLotPerimeterUpdateHandler && map) {
    map.off('draw.update', globalLotPerimeterUpdateHandler);
    map.off('draw.render', globalLotPerimeterUpdateHandler);
    globalLotPerimeterUpdateHandler = null;
  }
};